import { ThrowError } from "@/api/callApi";
import Card from "@/components/common/Card";
import CommonFilter from "@/components/common/CommonFilter";
import CommonModal from "@/components/common/CommonModal";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import HolidayCalendarCard from "@/components/common/Holidaycalendarcard ";
import MainHeading from "@/components/common/MainHeading";
import TablePagination from "@/components/common/TablePagination";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import type { holiday } from "@/interface/holiday.interface";
import { HolidayService } from "@/services/Holiday/HolidayService";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import {
  MoreHorizontal,
  PencilIcon,
  Plus,
  TrashIcon,
  TriangleAlert,
  Gift,
  PartyPopper,
  Smile,
  Sparkles,
  X,
  Flame,
} from "lucide-react";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const ISACTIVE_OPTIONS = [
  { label: "True", value: "true" },
  { label: "False", value: "false" },
];

interface HolidayProps {
  isAdmin?: boolean;
}

const Holiday: React.FC<HolidayProps> = ({ isAdmin = false }) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date(),
  );
  const [holiday, setHoliday] = useState<holiday[]>([]);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  // refs to hold debounce timer and current abort controller
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const [isTomorrowBannerDismissed, setIsTomorrowBannerDismissed] = useState(
    () => {
      if (isAdmin) return true;
      const key = `tomorrowHolidayBannerDismissed_${moment().format("YYYY-MM-DD")}`;
      return localStorage.getItem(key) === "true";
    },
  );

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    ...(isAdmin ? { isActive: true } : {}),
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();

  const handleDateRangeChange = useCallback((start: string, end: string) => {
    setFilter((prev) => ({ ...prev, startDate: start, endDate: end }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  }, []);
  const handleFilterChange = useCallback((value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const handleMonthChange = (date: Date) => {
    setCalendarMonth(date);

    const newStart = moment(date).startOf("month").format("YYYY-MM-DD");
    const newEnd = moment(date).endOf("month").format("YYYY-MM-DD");

    // clear previous debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // if call already in-flight then cancel it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // set debounce timer
    debounceRef.current = setTimeout(() => {
      setFilter((prev) => ({ ...prev, startDate: newStart, endDate: newEnd }));
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, 400);
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const handleGetHolidays = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const params = GetFilterUrl({ ...filter });
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${params ? `&${params}` : ""}`;
      const response = await HolidayService?.getHoliday(
        PATH,
        controller.signal,
      );
      if (response?.status) {
        setHoliday(response?.data?.holidays || response?.data?.holiday || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error: any) {
      if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
        ThrowError(error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const toggleDeleteDialog = (id: string | undefined) => {
    setDeleteId(id ?? "");
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleDeleteHoliday = async () => {
    try {
      const response = await HolidayService?.deleteHoliday(deleteId);
      if (response?.status) {
        showToastNotification("success", response?.message);
        toggleDeleteDialog("");
        handleGetHolidays();
      } else {
        showToastNotification("danger", response?.message);
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    handleGetHolidays(paginationData.currentPage, paginationData.pageSize);
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const parseDate = (dateStr?: string) => {
    if (!dateStr) return new Date();
    if (dateStr.includes("-")) {
      const parts = dateStr.split("T")[0].split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const [year, month, day] = parts.map(Number);
          return new Date(year, month - 1, day);
        } else {
          const [day, month, year] = parts.map(Number);
          return new Date(year, month - 1, day);
        }
      }
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  const holidayDates = holiday.map((item) => parseDate(item.date));

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const selectedHoliday = selectedDate
    ? holiday.find((item) => {
        const hDate = parseDate(item.date);
        return (
          hDate.getDate() === selectedDate.getDate() &&
          hDate.getMonth() === selectedDate.getMonth() &&
          hDate.getFullYear() === selectedDate.getFullYear()
        );
      })
    : undefined;

  const today = new Date();
  const todaysHoliday = !isAdmin
    ? holiday.find((item) => {
        const hDate = parseDate(item.date);
        return (
          hDate.getDate() === today.getDate() &&
          hDate.getMonth() === today.getMonth() &&
          hDate.getFullYear() === today.getFullYear()
        );
      })
    : undefined;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowHoliday = !isAdmin
    ? holiday.find((item) => {
        const hDate = parseDate(item.date);
        return (
          hDate.getDate() === tomorrow.getDate() &&
          hDate.getMonth() === tomorrow.getMonth() &&
          hDate.getFullYear() === tomorrow.getFullYear()
        );
      })
    : undefined;

  const tomorrowBannerStorageKey = `tomorrowHolidayBannerDismissed_${moment(today).format("YYYY-MM-DD")}`;

  const handleDismissTomorrowBanner = () => {
    localStorage.setItem(tomorrowBannerStorageKey, "true");
    setIsTomorrowBannerDismissed(true);
  };

  const columns: TableColumn<holiday>[] = [
    {
      key: "srNo",
      header: "Sr No",
      render: (_item: holiday, index?: number) => (
        <span className="text-muted-foreground font-medium">
          #{" "}
          {(index ?? 0) +
            1 +
            (paginationData.currentPage - 1) * paginationData.pageSize}
        </span>
      ),
    },
    {
      key: "date",
      header: "Date of Holidays",
      render: (item: holiday) => {
        return (
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {item?.date || "-"}
          </span>
        );
      },
    },
    {
      key: "name",
      header: "Holiday Name",
      render: (item: holiday) => {
        return (
          <span className="font-semibold text-primary capitalize">
            {item?.name || "-"}
          </span>
        );
      },
    },
    {
      key: "description",
      header: "Description",
      render: (item: holiday) => {
        return (
          <span className="text-muted-foreground">
            {item?.description || "-"}
          </span>
        );
      },
    },
    // status & actions columns only apply in Holiday Settings page for super admin
    ...(isAdmin
      ? [
          {
            key: "status",
            header: "Status",
            render: (item: holiday) => (
              <span
                className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isActive ? "text-green-600 border-green-600 bg-green-50" : "text-destructive border-destructive bg-destructive/10"}`}
              >
                {item?.isActive ? "Active" : "Inactive"}
              </span>
            ),
          },
          {
            key: "actions",
            header: "Actions",
            render: (item: holiday) => {
              const itemId = item.id || item._id || "";
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0 hover:bg-muted"
                    >
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="rounded-xl shadow-lg border-slate-100"
                  >
                    <DropdownMenuItem
                      className="cursor-pointer "
                      onClick={() =>
                        navigate(`${RoutesLink.editHoliday}/${itemId}`)
                      }
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer text-red-500"
                      onClick={() => toggleDeleteDialog(itemId)}
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          } as TableColumn<holiday>,
        ]
      : []),
  ];

  return (
    <>
      <Layout>
        <div className="space-y-6">
          <MainHeading
            title={isAdmin ? "Manage Holidays" : "Holidays"}
            buttonText={isAdmin ? "Add holiday" : undefined}
            buttonIcon={<Plus />}
            onClick={
              isAdmin ? () => navigate(RoutesLink.addHoliday) : undefined
            }
            isBackButtonVisible={true}
          />

          {!isAdmin && todaysHoliday && (
            <div className="relative overflow-hidden rounded-2xl border border-amber-300/60 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-500/10 dark:via-orange-500/10 dark:to-rose-500/10 px-5 py-4 shadow-sm">
              <Sparkles
                className="pointer-events-none absolute -top-3 -left-2 text-amber-400 opacity-70 animate-bounce [animation-duration:2.2s]"
                size={22}
                aria-hidden="true"
              />
              <Gift
                className="pointer-events-none absolute -bottom-3 right-10 text-rose-400 opacity-60 animate-bounce [animation-duration:2.6s] [animation-delay:.3s]"
                size={20}
                aria-hidden="true"
              />
              <PartyPopper
                className="pointer-events-none absolute top-1 right-4 text-orange-400 opacity-60 animate-bounce [animation-duration:1.9s] [animation-delay:.6s]"
                size={20}
                aria-hidden="true"
              />
              <div className="relative flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 animate-pulse">
                  <PartyPopper size={20} aria-hidden="true" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                    It's {todaysHoliday?.name} today — enjoy your holiday!
                    <Smile
                      className="text-amber-500"
                      size={16}
                      aria-hidden="true"
                    />
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Take the day to relax and recharge.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isAdmin &&
            !todaysHoliday &&
            tomorrowHoliday &&
            !isTomorrowBannerDismissed && (
              <div className="relative overflow-hidden rounded-2xl border border-purple-300 bg-purple-50 dark:bg-purple-500/10 dark:border-purple-500/40 px-5 py-4 pr-11 shadow-sm">
                <Flame
                  className="pointer-events-none absolute -top-3 -left-2 text-purple-600 opacity-80 animate-bounce [animation-duration:2.2s]"
                  size={22}
                  aria-hidden="true"
                />
                <Flame
                  className="pointer-events-none absolute -bottom-3 right-12 text-purple-600 opacity-70 animate-bounce [animation-duration:2.6s] [animation-delay:.3s]"
                  size={20}
                  aria-hidden="true"
                />
                <Flame
                  className="pointer-events-none absolute top-1 right-24 text-purple-600 opacity-70 animate-bounce [animation-duration:1.9s] [animation-delay:.6s]"
                  size={18}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  onClick={handleDismissTomorrowBanner}
                  aria-label="Dismiss"
                  className="absolute top-3 right-3 rounded-full p-1 text-muted-foreground/70 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                >
                  <X size={16} aria-hidden="true" />
                </button>
                <div className="relative flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400 animate-pulse">
                    <Flame size={20} aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                      Tomorrow is {tomorrowHoliday?.name} — finish your work
                      today so you can enjoy!
                      <Smile
                        className="text-purple-600"
                        size={16}
                        aria-hidden="true"
                      />
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Wrap up your tasks so you can fully enjoy your day off.
                    </p>
                  </div>
                </div>
              </div>
            )}

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_2fr] gap-6 items-start">
            <HolidayCalendarCard
              holiday={holiday}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              parseDate={parseDate}
              holidayDates={holidayDates}
              selectedHoliday={selectedHoliday}
              today={today}
              todaysHoliday={todaysHoliday}
              calendarMonth={calendarMonth}
              onMonthChange={handleMonthChange}
            />

            <Card>
              <div className="flex items-center justify-between border-b mb-6 pb-3">
                <div>
                  <h3 className="font-semibold text-base tracking-tight">
                    Holiday List
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Manage and view all upcoming company holidays
                  </p>
                </div>
              </div>
              <CommonFilter
                startDate={filter.startDate || ""}
                endDate={filter.endDate || ""}
                onDateRangeChange={handleDateRangeChange}
                searchValue={filter.search || ""}
                onChange={handleFilterChange}
                onSearchChange={handleSearchChange}
                {...(isAdmin
                  ? {
                      sortName: "isActive",
                      sortPlaceholder: "Is Active",
                      sortOptions: ISACTIVE_OPTIONS,
                    }
                  : {})}
              />
              <CommonTable
                columns={columns}
                data={holiday}
                loading={isLoading}
              />
              <TablePagination
                paginationData={paginationData}
                setPaginationData={setPaginationData}
                handleChangePageSize={handleChangePageSize}
                isLoading={isLoading}
              />
            </Card>
          </div>
        </div>
      </Layout>

      {isAdmin && (
        <CommonModal
          title=""
          show={deleteDialogOpen}
          onClose={() => toggleDeleteDialog("")}
          className="max-w-md rounded-2xl p-0 overflow-hidden"
        >
          <div className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <TriangleAlert className="text-red-600 w-8 h-8" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-gray-900">
                Delete Holiday?
              </h2>
              <p className="mt-3 text-sm leading-6 text-gray-500">
                This action cannot be undone. Once deleted, this holiday will be
                permanently removed from the company calendar.
              </p>
            </div>
            <div className="mt-8 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => toggleDeleteDialog("")}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteHoliday}
              >
                Delete
              </Button>
            </div>
          </div>
        </CommonModal>
      )}
    </>
  );
};

export default Holiday;
